import { Link, useLoaderData } from 'react-router'
import {
	getEventLiveStatus,
	getLiveEvents,
	getShowWinners,
	getTourDates,
} from '#app/utils/dmapi.server.ts'
import { isDatePassedUTC } from '#app/utils/misc.tsx'
import { type Route } from './+types/tour._index.ts'

export const meta: Route.MetaFunction = () => {
	const title = 'Tour Dates | DanceMakers'
	const description =
		'View all DanceMakers convention and competition tour dates. Find an event near you and register today.'
	const siteUrl = 'https://dancemakersinc.com'

	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: `${siteUrl}/tour` },
	]
}

type TourEvent = {
	id: string
	name: string
	slug: string
	dates: string
	startDate: string | null
	endDate: string | null
	venue: string | null
	cutoff: string | null
}

type TourData = {
	tour: { id: string; name: string; years: string } | null
	events: TourEvent[]
}

// Event status info from live events
type EventStatusInfo = {
	isScheduleReady: boolean
	showWinners: boolean
}

export async function loader() {
	const apiUrl = process.env.DMAPI_URL || 'http://localhost:3001'

	try {
		// Fetch tour dates and live events in parallel
		const [tourData, liveEventsResponse] = await Promise.all([
			getTourDates<TourData>(apiUrl),
			getLiveEvents(apiUrl),
		])

		if (!tourData) {
			return {
				tourData: null,
				eventStatusMap: {} as Record<string, EventStatusInfo>,
			}
		}

		// Build a map of event status info by event ID
		// First, populate from live events data
		const eventStatusMap: Record<string, EventStatusInfo> = {}
		for (const event of liveEventsResponse.events) {
			eventStatusMap[event.eventId] = {
				isScheduleReady: event.isScheduleReady,
				showWinners: event.showWinners ?? false,
			}
		}

		// For events not in live events, fetch schedule status and showWinners
		// Only check for events where registration is closed
		const eventsToCheck = tourData.events.filter(
			(e) => !eventStatusMap[e.id] && isDatePassedUTC(e.cutoff),
		)

		if (eventsToCheck.length > 0) {
			// Fetch both schedule status and showWinners in parallel for each event
			const statusResults = await Promise.all(
				eventsToCheck.map(async (e) => {
					const [liveStatus, showWinners] = await Promise.all([
						getEventLiveStatus(apiUrl, e.id),
						getShowWinners(apiUrl, e.id),
					])
					return {
						id: e.id,
						isScheduleReady: liveStatus?.isScheduleReady ?? false,
						showWinners,
					}
				}),
			)
			statusResults.forEach((result) => {
				eventStatusMap[result.id] = {
					isScheduleReady: result.isScheduleReady,
					showWinners: result.showWinners,
				}
			})
		}

		return { tourData, eventStatusMap }
	} catch (error) {
		console.error('Error fetching tour dates:', error)
		return {
			tourData: null,
			eventStatusMap: {} as Record<string, EventStatusInfo>,
		}
	}
}

// Format event date range using UTC to avoid timezone issues
function formatEventDates(event: TourEvent): string {
	if (!event.startDate) return event.dates // Fall back to pre-formatted string if no startDate

	const startDate = new Date(event.startDate)
	const endDate = event.endDate ? new Date(event.endDate) : null

	const startMonth = startDate.toLocaleDateString('en-US', {
		month: 'short',
		timeZone: 'UTC',
	})
	const startDay = startDate.toLocaleDateString('en-US', {
		day: 'numeric',
		timeZone: 'UTC',
	})

	if (!endDate) {
		return `${startMonth} ${startDay}`
	}

	const endMonth = endDate.toLocaleDateString('en-US', {
		month: 'short',
		timeZone: 'UTC',
	})
	const endDay = endDate.toLocaleDateString('en-US', {
		day: 'numeric',
		timeZone: 'UTC',
	})

	// Same month: "Nov 8-10"
	if (startMonth === endMonth) {
		return `${startMonth} ${startDay}-${endDay}`
	}

	// Different months: "Nov 30 - Dec 1"
	return `${startMonth} ${startDay} - ${endMonth} ${endDay}`
}

export default function TourIndex() {
	const { tourData, eventStatusMap } = useLoaderData<typeof loader>()
	const events = tourData?.events ?? []
	const tourName = tourData?.tour?.name ?? '2025 Season'
	const tourYears = tourData?.tour?.years ?? '2025'

	return (
		<div className="min-h-screen pb-16">
			{/* Hero Section with Video Background */}
			<section className="relative flex min-h-[70vh] items-center overflow-hidden">
				{/* Video Background */}
				<video
					autoPlay
					muted
					loop
					playsInline
					className="absolute inset-0 h-full w-full object-cover"
					poster="/assets/video/tour-banner.jpg"
				>
					<source src="/videos/fun-again-640-hq.webm" type="video/webm" />
					<source src="/videos/fun-again-640-hq.mp4" type="video/mp4" />
				</video>

				{/* Dark Overlay */}
				<div className="absolute inset-0 bg-black/40" />

				{/* Content */}
				<div className="container relative z-10 px-6 py-24 pt-32 md:px-16">
					<p className="text-xs font-medium uppercase tracking-[0.2em] text-background md:text-sm">
						{tourYears} Tour
					</p>
					<h1 className="mb-4 font-display text-2xl font-bold uppercase text-white md:text-5xl">
						{tourName}
					</h1>
					<div className="max-w-xl space-y-2 text-[12px] leading-tight text-background md:text-[14px]">
						<div>
							Remember when dance was just fun?
							<span className="block">Let’s do that again.</span>
						</div>
						<div>
							This is your invitation to feel free, stay present, and enjoy
							every moment.
						</div>
						<div>
							Dance full out.<span className="block">Laugh loud.</span>
							<span className="block">Love it all.</span>
						</div>
						<div>
							No pressure. Just passion.
							<span className="block">Let's have fun again.</span>
						</div>
					</div>
				</div>
			</section>

			{/* Tour Dates List */}
			<section className="container px-6 py-12 md:px-16">
				{events.length > 0 ? (
					<div className="grid gap-4">
						{events.map((event, index) => {
							const isRegistrationClosed = isDatePassedUTC(event.cutoff)
							const eventStatus = eventStatusMap[event.id]
							const isScheduleReleased = eventStatus?.isScheduleReady ?? false
							const showWinners = eventStatus?.showWinners ?? false

							return (
								<div
									key={event.id}
									className="group flex flex-col justify-between border border-border/50 bg-card/50 p-6 transition-all duration-300 hover:border-primary/50 hover:bg-card md:flex-row md:items-center"
									style={{ animationDelay: `${index * 0.05}s` }}
								>
									<div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-12">
										<span className="min-w-[120px] text-sm font-bold uppercase tracking-wider text-primary">
											{formatEventDates(event)}
										</span>
										<Link
											to={`/tour/${event.slug}`}
											className="font-display text-xl font-bold uppercase transition-colors hover:text-primary md:text-2xl"
										>
											{event.name}
										</Link>
										{event.venue && (
											<span className="text-sm text-muted-foreground">
												{event.venue}
											</span>
										)}
									</div>
									{showWinners ? (
										<Link
											to={`/tour/${event.slug}/results`}
											className="mt-4 flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-primary transition-colors hover:text-primary/80 md:mt-0"
										>
											Results
											<svg
												className="h-4 w-4"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M9 5l7 7-7 7"
												/>
											</svg>
										</Link>
									) : isRegistrationClosed ? (
										isScheduleReleased ? (
											<Link
												to={`/tour/${event.slug}/schedule`}
												className="mt-4 flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-primary transition-colors hover:text-primary/80 md:mt-0"
											>
												View Schedule
												<svg
													className="h-4 w-4"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M9 5l7 7-7 7"
													/>
												</svg>
											</Link>
										) : (
											<span className="mt-4 text-sm font-medium uppercase tracking-wider text-muted-foreground/50 md:mt-0">
												Reg Closed
											</span>
										)
									) : (
										<a
											href="https://dancemakersinc.io"
											target="_blank"
											rel="noopener noreferrer"
											className="mt-4 flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-foreground/60 transition-colors hover:text-accent md:mt-0"
										>
											Register
											<svg
												className="h-4 w-4"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
												/>
											</svg>
										</a>
									)}
								</div>
							)
						})}
					</div>
				) : (
					<div className="py-24 text-center">
						<h2
							className="mb-4 text-xl font-bold uppercase sm:text-2xl"
							style={{ fontFamily: 'var(--font-display)' }}
						>
							Tour Dates Coming Soon
						</h2>
						<p className="text-muted-foreground">
							Check back soon for our upcoming tour schedule.
						</p>
					</div>
				)}
			</section>
		</div>
	)
}
